import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check, X, ArrowRight, RotateCcw, Trophy } from 'lucide-react';

const quizQuestions = [
  {
    type: 'vf',
    question: 'Dans la méthode RePas, certains aliments sont interdits.',
    answer: false,
    explanation: 'Aucun aliment n\'est interdit. La privation crée l\'obsession, qui crée la compulsion. C\'est l\'inverse des régimes classiques.'
  },
  {
    type: 'qcm',
    question: 'L\'anticipation du plaisir alimentaire consiste à :',
    options: [
      'Se priver en imaginant ce qu\'on aimerait manger',
      'Imaginer son repas en détail avec les 5 sens, puis le manger vraiment',
      'Compter les calories de son futur repas'
    ],
    answer: 1,
    explanation: 'En imaginant votre repas avec vos 5 sens puis en le mangeant vraiment, vous doublez le plaisir et réduisez les quantités naturellement.'
  },
  {
    type: 'qcm',
    question: 'Combien de repas par jour la méthode recommande-t-elle ?',
    options: [
      '1 seul repas copieux',
      '2 ou 3 repas fixes, sans encas',
      '5 petits repas répartis dans la journée'
    ],
    answer: 1,
    explanation: 'Des repas fixes et réguliers permettent de réguler la faim, d\'éviter le grignotage et de favoriser la combustion des graisses.'
  },
  {
    type: 'vf',
    question: 'La "règle des 7", c\'est imaginer 7 fois l\'aliment qui vous tente.',
    answer: false,
    explanation: 'Il faut le manger vraiment 7 fois. C\'est le dégoût réel qui casse la compulsion, pas la visualisation.'
  },
  {
    type: 'qcm',
    question: 'Face à une envie hors repas, quelles sont vos deux options ?',
    options: [
      'Résister et culpabiliser / Craquer et culpabiliser',
      '"Oui, mais plus tard" / "Oui, mais 7 fois maintenant"',
      'Manger un peu / Boire un verre d\'eau'
    ],
    answer: 1,
    explanation: '"Plus tard" fait souvent disparaître l\'envie. "7 fois" crée un dégoût qui casse la compulsion. Dans les deux cas, zéro culpabilité.'
  },
  {
    type: 'vf',
    question: 'Pour l\'activité physique, il faut choisir un sport intense pour brûler un maximum de calories.',
    answer: false,
    explanation: 'L\'important est de bouger avec plaisir. Marcher, danser ou jardiner suffit. Le plaisir garantit la régularité.'
  },
  {
    type: 'qcm',
    question: 'Pourquoi manger dans des assiettes à dessert ?',
    options: [
      'Pour se punir de trop manger',
      'Pour tromper le cerveau : la portion paraît plus grande et rassasie plus vite',
      'Pour limiter les glucides'
    ],
    answer: 1,
    explanation: 'C\'est l\'illusion de Delboeuf : une même portion paraît plus généreuse dans une petite assiette. Votre cerveau est rassasié plus vite.'
  },
  {
    type: 'vf',
    question: 'Un écart alimentaire signifie que vous avez échoué.',
    answer: false,
    explanation: 'L\'écart fait partie du processus. C\'est la réaction (culpabilité) qui pose problème, pas l\'écart lui-même. Patience et tolérance.'
  },
  {
    type: 'qcm',
    question: 'La méthode RePas agit principalement sur :',
    options: [
      'La volonté et la discipline',
      'L\'inconscient et les mécanismes automatiques du cerveau',
      'Le comptage des calories'
    ],
    answer: 1,
    explanation: 'Les séances d\'hypnose reprogramment l\'inconscient, les déclics transforment votre façon de penser. C\'est la combinaison des deux qui crée le changement.'
  },
  {
    type: 'vf',
    question: 'Il est important de se peser tous les jours pour mesurer ses progrès.',
    answer: false,
    explanation: 'Le suivi se fait via l\'énergie, la digestion, le sommeil et les réussites concrètes. Le poids n\'est qu\'un indicateur parmi d\'autres.'
  },
  {
    type: 'vf',
    question: 'J\'ai le droit de me resservir quand je mange dans une petite assiette.',
    answer: true,
    explanation: 'Bien sûr ! L\'assiette à dessert trompe votre cerveau en lui donnant l\'impression d\'abondance. Si après cette première assiette vous avez encore faim, resservez-vous — même plusieurs fois. L\'objectif n\'est jamais de vous restreindre.'
  }
];

export const Quiz = () => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [quizFinished, setQuizFinished] = useState(false);
  const [answers, setAnswers] = useState([]);

  const q = quizQuestions[currentQuestion];

  const handleAnswer = (answer) => {
    if (showResult) return;
    setSelectedAnswer(answer);
    setShowResult(true);

    const isCorrect = q.type === 'vf' ? answer === q.answer : answer === q.answer;
    if (isCorrect) setScore(s => s + 1);
    setAnswers(prev => [...prev, { question: currentQuestion, answer, isCorrect }]);
  };

  const nextQuestion = () => {
    if (currentQuestion + 1 < quizQuestions.length) {
      setCurrentQuestion(c => c + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      setQuizFinished(true);
    }
  };

  const restartQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setQuizFinished(false);
    setAnswers([]);
  };

  const isCorrect = (answer) => {
    if (q.type === 'vf') return answer === q.answer;
    return answer === q.answer;
  };

  if (quizFinished) {
    const percentage = Math.round((score / quizQuestions.length) * 100);
    return (
      <div className="min-h-screen bg-sand-50 px-6 py-8" data-testid="quiz-results">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-sage-100">
            <Trophy className={`w-16 h-16 mx-auto mb-4 ${percentage >= 70 ? 'text-yellow-500' : 'text-sage-400'}`} />
            <h2 className="text-2xl font-serif text-sage-700 mb-2">
              {percentage >= 90 ? 'Excellent !' : percentage >= 70 ? 'Bravo !' : 'Continuez !'}
            </h2>
            <p className="text-4xl font-serif text-sage-700 mb-2">
              {score}/{quizQuestions.length}
            </p>
            <p className="text-sage-500 text-sm mb-6">
              {percentage >= 90
                ? 'Vous maîtrisez parfaitement la méthode. Vous êtes prêt(e) !'
                : percentage >= 70
                ? 'Vous avez bien compris l\'essentiel. Relisez les déclics qui vous ont posé problème.'
                : 'Prenez le temps de relire les déclics. La compréhension vient avec la pratique.'
              }
            </p>

            <div className="space-y-3">
              <Button
                onClick={restartQuiz}
                variant="outline"
                className="w-full h-12 rounded-xl border-sage-300 text-sage-600"
                data-testid="quiz-restart-button"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Recommencer le quiz
              </Button>
              <Button
                onClick={() => navigate('/methode')}
                className="w-full h-12 rounded-xl bg-sage-600 hover:bg-sage-600 text-white"
                data-testid="quiz-back-method-button"
              >
                Retour à la méthode
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50 px-6 py-8 flex flex-col" data-testid="quiz-page">
      <div className="max-w-md mx-auto w-full flex-1 flex flex-col">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-sage-500 mb-2">
            <span>Question {currentQuestion + 1}/{quizQuestions.length}</span>
            <span>{score} bonne{score > 1 ? 's' : ''} réponse{score > 1 ? 's' : ''}</span>
          </div>
          <div className="w-full bg-sage-200 rounded-full h-2">
            <div
              className="bg-sage-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / quizQuestions.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-sage-100 mb-4">
          <div className="inline-block bg-sage-100 text-sage-600 text-xs font-medium px-3 py-1 rounded-full mb-4">
            {q.type === 'vf' ? 'Vrai ou Faux' : 'Choix multiple'}
          </div>
          <h2 className="text-lg font-medium text-sage-700 leading-relaxed" data-testid="quiz-question-text">
            {q.question}
          </h2>
        </div>

        {/* Answers */}
        <div className="space-y-3 mb-4">
          {q.type === 'vf' ? (
            <>
              {[true, false].map((val) => {
                const isSelected = selectedAnswer === val;
                const isRight = val === q.answer;
                let btnClass = 'bg-white border-sage-200 text-sage-700 hover:border-sage-400';
                if (showResult && isSelected && isRight) btnClass = 'bg-green-50 border-green-400 text-green-700';
                if (showResult && isSelected && !isRight) btnClass = 'bg-red-50 border-red-400 text-red-700';
                if (showResult && !isSelected && isRight) btnClass = 'bg-green-50 border-green-300 text-green-700';

                return (
                  <button
                    key={String(val)}
                    onClick={() => handleAnswer(val)}
                    disabled={showResult}
                    className={`w-full p-4 rounded-xl border-2 text-left font-medium transition-all flex items-center justify-between ${btnClass}`}
                    data-testid={`quiz-answer-${val}`}
                  >
                    <span>{val ? 'Vrai' : 'Faux'}</span>
                    {showResult && isRight && <Check className="w-5 h-5 text-green-500" />}
                    {showResult && isSelected && !isRight && <X className="w-5 h-5 text-red-500" />}
                  </button>
                );
              })}
            </>
          ) : (
            <>
              {q.options.map((option, idx) => {
                const isSelected = selectedAnswer === idx;
                const isRight = idx === q.answer;
                let btnClass = 'bg-white border-sage-200 text-sage-700 hover:border-sage-400';
                if (showResult && isSelected && isRight) btnClass = 'bg-green-50 border-green-400 text-green-700';
                if (showResult && isSelected && !isRight) btnClass = 'bg-red-50 border-red-400 text-red-700';
                if (showResult && !isSelected && isRight) btnClass = 'bg-green-50 border-green-300 text-green-700';

                return (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(idx)}
                    disabled={showResult}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${btnClass}`}
                    data-testid={`quiz-answer-${idx}`}
                  >
                    <span className="text-sm leading-relaxed">{option}</span>
                    {showResult && isRight && <Check className="w-5 h-5 text-green-500 flex-shrink-0 ml-2" />}
                    {showResult && isSelected && !isRight && <X className="w-5 h-5 text-red-500 flex-shrink-0 ml-2" />}
                  </button>
                );
              })}
            </>
          )}
        </div>

        {/* Explanation */}
        {showResult && (
          <div className={`p-4 rounded-xl mb-4 ${isCorrect(selectedAnswer) ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
            <p className="text-sm text-sage-700 leading-relaxed">
              {q.explanation}
            </p>
          </div>
        )}

        {/* Next button */}
        {showResult && (
          <Button
            onClick={nextQuestion}
            className="w-full h-12 rounded-xl bg-sage-600 hover:bg-sage-600 text-white"
            data-testid="quiz-next-button"
          >
            {currentQuestion + 1 < quizQuestions.length ? (
              <>Question suivante <ArrowRight className="w-4 h-4 ml-2" /></>
            ) : (
              <>Voir mes résultats <Trophy className="w-4 h-4 ml-2" /></>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default Quiz;
